import { useMemo, useState } from 'react';
import type { ScopedSamples } from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import {
	buildScopedLiquidExpression,
	filterDataPoints,
	groupDataPoints,
	previewFor,
} from './bindPanel.utils.ts';
import { scopedPointsFor, sourceForScope } from './scopedBinding.utils.ts';
import { restoreSecurityState, selectedDataPointsOf, togglePath } from './bindingBuilder.utils.ts';
import type {
	DataPointWithSampleRes,
	JoinWith,
	ScopedSource,
	SecurityOptionRes,
	TransactionScope,
} from './BindPanel.types.ts';
import type { SecurityBinding } from './bindingBuilder.types.ts';

/**
 * Security mode: one pinned programme security, read from its holdings or its in-flight
 * allocation depending on the transaction radio.
 */
export const useSecurityBinding = ({
	bindingTemplate,
	securities,
	scopedSamples,
	search,
	onChange,
}: {
	bindingTemplate: string | null;
	securities: SecurityOptionRes[];
	scopedSamples: ScopedSamples;
	search: string;
	onChange: (bindingTemplate: string) => void;
}): SecurityBinding => {
	const initial = useMemo(
		() => restoreSecurityState(bindingTemplate, securities),
		[bindingTemplate, securities]
	);
	const [securityId, setSecurityId] = useState(initial.securityId);
	const [transactionScope, setTransactionScope] = useState<TransactionScope>(
		initial.transactionScope
	);
	/**
	 * The selected paths PER SOURCE, not one list shared by both radio positions.
	 *
	 * The two positions offer different points, so switching had to drop the ones the new
	 * source cannot resolve — and it dropped them from the only copy. With no paths left,
	 * `buildScopedLiquidExpression` returns `''` ([].join), so one look at the other position
	 * destroyed the binding and switching back could not bring it back. Each source keeping its
	 * own selection makes the radio a view again rather than an edit.
	 */
	const [pathsBySource, setPathsBySource] = useState<Record<ScopedSource, string[]>>(() => ({
		holdings: initial.transactionScope === 'completed' ? initial.paths : [],
		allocations: initial.transactionScope === 'in-progress' ? initial.paths : [],
	}));
	const [joinWith, setJoinWith] = useState<JoinWith>(initial.joinWith);

	const source = sourceForScope(transactionScope);
	const selectedPaths = pathsBySource[source];

	/**
	 * The scoped catalogue: the points this radio position offers, with samples for the pin.
	 *
	 * An in-progress point says so in its LABEL, the way the positional entries do: its
	 * expression is a `| first` over `allocations`, which can legitimately hold two rows for one
	 * security (two in-flight workflows, or a correction pair in one), and every other row is
	 * dropped. `holdings` carries one row per security, so its `| first` is exact.
	 */
	const catalogue = useMemo<DataPointWithSampleRes[]>(
		() =>
			scopedPointsFor(transactionScope).map((point) => ({
				group: point.group,
				label:
					source === 'allocations'
						? `${point.label} (first allocation only)`
						: point.label,
				path: point.path,
				sampleValue: scopedSamples[securityId]?.[source]?.[point.path] ?? '',
				optional: true,
			})),
		[transactionScope, source, scopedSamples, securityId]
	);

	const emit = (parts: {
		securityId?: string;
		transactionScope?: TransactionScope;
		paths?: string[];
		joinWith?: JoinWith;
	}) =>
		onChange(
			buildScopedLiquidExpression({
				securityId: parts.securityId ?? securityId,
				source: sourceForScope(parts.transactionScope ?? transactionScope),
				paths: parts.paths ?? selectedPaths,
				joinWith: parts.joinWith ?? joinWith,
			})
		);

	const applyPaths = (next: string[]) => {
		setPathsBySource((prev) => ({ ...prev, [source]: next }));
		emit({ paths: next });
	};

	const selectedDataPoints = useMemo(
		() => selectedDataPointsOf(selectedPaths, catalogue),
		[selectedPaths, catalogue]
	);
	const groups = useMemo(
		() => groupDataPoints(filterDataPoints(catalogue, search)),
		[catalogue, search]
	);
	const preview = useMemo(
		() => previewFor(selectedPaths, catalogue, joinWith),
		[selectedPaths, catalogue, joinWith]
	);

	return {
		securityId,
		transactionScope,
		selectedPaths,
		selectedDataPoints,
		groups,
		preview,
		joinWith,
		onChangeSecurity: (id) => {
			setSecurityId(id);
			emit({ securityId: id });
		},
		onChangeTransactionScope: (next) => {
			setTransactionScope(next);
			const nextSource = sourceForScope(next);
			const remembered = pathsBySource[nextSource];
			// Two rules, and both matter. A position that already has its own selection keeps
			// it — so leaving one and coming back is a view change, not an edit. A position
			// with nothing selected yet inherits whatever the current one holds that it can
			// ALSO resolve, which is what makes flipping the radio rewrite an `either` point's
			// source (its whole purpose) instead of clearing it. What the target cannot
			// resolve — a holdings-only path like `unitsHeld` — stays behind, remembered.
			const resolvable = new Set(scopedPointsFor(next).map((point) => point.path));
			const paths =
				remembered.length > 0
					? remembered
					: selectedPaths.filter((path) => resolvable.has(path));
			setPathsBySource((prev) => ({ ...prev, [nextSource]: paths }));
			emit({ transactionScope: next, paths });
		},
		onToggleDataPoint: (path) => applyPaths(togglePath(selectedPaths, path)),
		onRemoveDataPoint: (path) =>
			applyPaths(selectedPaths.filter((selected) => selected !== path)),
		onChangeJoinWith: (next) => {
			setJoinWith(next);
			emit({ joinWith: next });
		},
		apply: () => emit({}),
	};
};
